/**
 * useTranslation Hook
 * Provides a simple interface for translations with fallback support
 */

import { useLanguage } from '../contexts/LanguageContext';

/**
 * Custom hook for translations with enhanced features
 * @returns {Object} Translation utilities
 */
export const useTranslation = () => {
  const { t, currentLanguage, changeLanguage, getAvailableLanguages, getCurrentLanguageInfo } = useLanguage();

  /**
   * Translate with parameters substitution
   * Example: tp('welcome.message', { name: 'John' }) with template "Hello {{name}}"
   */
  const tp = (key, params = {}, fallback = '') => {
    let translation = t(key, fallback);
    
    // Replace parameters in the translation
    if (params && typeof params === 'object') {
      Object.keys(params).forEach(paramKey => {
        const regex = new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g');
        translation = translation.replace(regex, params[paramKey]);
      });
    }
    
    return translation;
  };

  /**
   * Translate with pluralization
   * Example: tn('items', 5) will use 'items.plural' if count !== 1
   */
  const tn = (key, count, fallback = '') => {
    if (count === 1) {
      return t(`${key}.singular`, fallback);
    } else {
      return t(`${key}.plural`, fallback);
    }
  };

  /**
   * Check if a translation exists
   */
  const hasTranslation = (key) => {
    const translation = t(key);
    return translation !== key; // If returns key, translation doesn't exist
  };

  /**
   * Get current language code
   */
  const lang = currentLanguage;

  /**
   * Check if current language is RTL
   */
  const isRTL = () => {
    const langInfo = getCurrentLanguageInfo();
    return langInfo?.rtl || false;
  };

  return {
    t,           // Standard translation
    tp,          // Translation with parameters
    tn,          // Translation with pluralization
    hasTranslation,
    lang,        // Current language code
    isRTL,       // Check if RTL
    changeLanguage,
    getAvailableLanguages,
    getCurrentLanguageInfo
  };
};

export default useTranslation;
